/**
 * 構造化データ（JSON-LD）管理ユーティリティ
 */

/**
 * JSON-LDスクリプトを追加または更新
 */
export const setStructuredData = (id: string, data: object): void => {
  if (typeof document === 'undefined') return;

  // 既存のスクリプトを削除
  const existingScript = document.getElementById(`structured-data-${id}`);
  if (existingScript) {
    existingScript.remove();
  }

  // 新しいスクリプトを追加
  const script = document.createElement('script');
  script.id = `structured-data-${id}`;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * JSON-LDスクリプトを削除
 */
export const removeStructuredData = (id: string): void => {
  if (typeof document === 'undefined') return;

  const script = document.getElementById(`structured-data-${id}`);
  if (script) {
    script.remove();
  }
};

/**
 * Article構造化データを生成
 */
export interface ArticleStructuredData {
  headline: string;
  description: string;
  image?: string;
  datePublished?: string;  // オプショナル（下書きの場合は存在しない）
  dateModified?: string;
  author: {
    name: string;
    url?: string;
  };
  publisher: {
    name: string;
    logo?: {
      url: string;
      width?: number;
      height?: number;
    };
  };
  url: string;
}

export const createArticleStructuredData = (data: ArticleStructuredData): object => {
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : '';

  const structuredData: any = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.headline,
    description: data.description,
    url: data.url,
    author: {
      '@type': 'Person',
      name: data.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: data.publisher.name,
    },
  };

  if (data.author.url) {
    structuredData.author.url = data.author.url;
  }

  if (data.datePublished) {
    structuredData.datePublished = data.datePublished;
  }

  if (data.image) {
    const imageUrl = data.image.startsWith('http') 
      ? data.image 
      : `${siteUrl}${data.image}`;
    structuredData.image = imageUrl;
  }

  if (data.dateModified) {
    structuredData.dateModified = data.dateModified;
  }

  if (data.publisher.logo) {
    structuredData.publisher.logo = {
      '@type': 'ImageObject',
      url: data.publisher.logo.url,
    };
    if (data.publisher.logo.width) {
      structuredData.publisher.logo.width = data.publisher.logo.width;
    }
    if (data.publisher.logo.height) {
      structuredData.publisher.logo.height = data.publisher.logo.height;
    }
  }

  return structuredData;
};

/**
 * BreadcrumbList構造化データを生成
 */
export interface BreadcrumbItem {
  name: string;
  url: string;
}

export const createBreadcrumbStructuredData = (items: BreadcrumbItem[]): object => {
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : '';

  const breadcrumbList = items.map((item, index) => {
    const itemUrl = item.url.startsWith('http') 
      ? item.url 
      : `${siteUrl}${item.url}`;

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: itemUrl,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbList,
  };
};

/**
 * WebSite構造化データを生成
 */
export interface WebSiteStructuredData {
  name: string;
  url: string;
  description?: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

export const createWebSiteStructuredData = (data: WebSiteStructuredData): object => {
  const structuredData: any = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: data.name,
    url: data.url,
  };

  if (data.description) {
    structuredData.description = data.description;
  }

  if (data.potentialAction) {
    structuredData.potentialAction = data.potentialAction;
  }

  return structuredData;
};

